// 레이블리 아카데미(명칭 임시) — 유튜브 쇼츠 기초 7주 과정. 레슨 8세트(OT + 1~7주차) + 심화 링크 (순수 함수 · IO 없음)
// 기획: docs/ops/labely-academy-plan.md (§5 공개 규칙 · §6 레슨 데이터 · §7 데이터 모델)
// 원본: docs/ops/labely-academy/curriculum-2026-09-03.html · lesson-cards.md (카톡 대화방 카드 9장)
//
// 화면은 첫 신청 레슨 모달(WorkLessonModal)을 그대로 쓴다 — 세트마다 [상황 문항 장 → 완료 장] 두 장.
// 무료 강의 링크·이번 주 과제는 /academy 페이지 상세가 보여 주고, 모달의 완료 장은 가이드 링크만 단다.
//
// 🔒 문항 정답은 예/아니요가 한쪽으로 쏠리면 안 된다 — 지금 「예」 3(OT·4·6) / 「아니요」 5. 테스트가 이 균형을 지킨다.
// 🔒 음원·음악 관련 내용은 넣지 않는다 (대표 지시 2026-09-22). 심화 트랙도 마찬가지. 테스트가 단어를 스캔한다.
// 🔒 정책 수치(YPP 조건 · 2027-02-01)는 고객센터 1차 출처를 같은 장에 적는다. 배포 직전 재확인(8월 갱신 기준).
// 🔒 실명·작품명·채널명·권리사명 금지. 강의 제작자·강의 제목은 출처 표기라 그대로 둔다.

import { tt } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";

export const ACADEMY_TRIGGER = "academy"; // work_lesson_completion.trigger (마이그레이션 20260923000000 의 check 목록과 같이 유지)
export const ACADEMY_VERSION = "v1.0";
export const ACADEMY_WEEK_COUNT = 8; // OT + 7주차 (2026-09-22 대표 지시: 음원 관련 레슨 전부 제외 — 옛 5주차 삭제, 뒤 주차 번호 당김)
export const ACADEMY_LESSON_KEYS = Array.from({ length: ACADEMY_WEEK_COUNT }, (_, i) => `academy_w${i}`);
export const ACADEMY_ROUTE = "/academy";
export const ACADEMY_GUIDE_SLUG = "c-academy";
export const ACADEMY_POLICY_DEADLINE = "2027-02-01"; // YPP 신규 진입 기준 2배 · 쇼츠 광고 수익 90일 1,000만 유지 조건
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const academyLessonKey = (i) => ACADEMY_LESSON_KEYS[i] || null;

/**
 * 누가 보나(기획 §3) — 크리에이터 전원 + 어드민(테스트 경로, 등급 가점 카드와 동일). 파트너 전용·영상권리사·플랫폼 계정은 아니다.
 * 페이지 · 홈 카드 · 설정 메뉴가 전부 이 한 곳을 쓴다.
 */
export function isAcademyViewer(role) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  if (r.includes("platform")) return false;
  return r.includes("creator") || r.includes("admin");
}
export const academyWeekIndexOf = (lessonKey) => {
  const i = ACADEMY_LESSON_KEYS.indexOf(lessonKey);
  return i < 0 ? null : i;
};
export const isAcademyLessonKey = (lessonKey) => ACADEMY_LESSON_KEYS.includes(lessonKey);

export const academyWeekLabel = (i) => (i === 0 ? "OT" : tt("a.academy.week", "{{v0}}주차", { v0: i }));

const src = (label, meta, href, kind = "video") => ({ label, meta, href, kind });

/** 주차 정의 — title·goal·points(핵심 3)·sources(무료 강의)·task(과제)·chapters(모달 장 2개) */
export const ACADEMY_WEEKS = lazyI18n(() => [
  {
    index: 0,
    key: ACADEMY_LESSON_KEYS[0],
    title: tt("a.academy.w0.title", "왜 지금, 무엇을 목표로"),
    goal: tt("a.academy.w0.goal", "과정 소개와 2027년 2월 정책 변화. 내 채널의 현재 위치를 확인합니다."),
    points: [
      tt("a.ab012292", "2027년 2월 1일부터 유튜브 파트너 프로그램 신규 진입 기준이 **2배**가 됩니다(롱폼 4,000 → 8,000시간 · 90일 쇼츠 유효 조회 1,000만 → 2,000만). **2027년 1월 31일까지 가입하면 기존 기준**이 적용됩니다."),
      tt("a.8fc1199f", "쇼츠 광고 수익은 100만 뷰에 수만 원 수준입니다. 조회수가 아니라 쇼핑·팬 펀딩·롱폼으로 이어지는 구조를 만드는 것이 이 과정의 목표입니다."),
      tt("a.45df5ce9", "이미 파트너라면 스튜디오에서 개정 약관 동의(2027-01-31까지)와 쇼츠 수익 창출 모듈 수락 여부를 확인하세요."),
    ],
    sources: [
      src("유튜브 훈련소 · 바뀌는 수익창출 조건에서 살아남는 법", tt("a.167ac5f4", "11분"), "https://www.youtube.com/watch?v=2jy_r_UMA_w"),
      src("유튜브 공식 · 수익 창출 방식", tt("a.1c73f88a", "문서"), "https://www.youtube.com/intl/ko/creators/how-things-work/video-monetization/", "doc"),
      src("유튜브 공식 블로그 · 2027 파트너 프로그램 변경", tt("a.ed10bc94", "1차 출처"), "https://blog.youtube/intl/ko-kr/news-and-events/youtube-partner-program-updates-2027-new-opportunities-earn/", "doc"),
    ],
    task: tt("a.364282c2", "유튜브 스튜디오에서 ① 구독자 수 ② 최근 90일 쇼츠 조회수 ③ 최근 12개월 시청시간 화면을 캡처해 대화방에 올려 주세요. 8주 뒤 같은 화면과 비교합니다."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w0.kicker", "OT · 왜 지금"),
        title: tt("a.academy.w0.title", "왜 지금, 무엇을 목표로"),
        tone: "critical",
        core: tt("a.52a50154", "**2027년 2월 1일**부터 유튜브 파트너 프로그램 신규 진입 기준이 두 배가 됩니다. 1월 31일까지 가입하면 기존 기준이 그대로입니다."),
        caseText: tt("a.ffdbd770", "쇼츠만 올리던 채널이 12월에 「조금만 더 모아서 신청하자」며 미뤘다가, 2월에 기준이 두 배로 오른 뒤 **1년 넘게 조건을 못 채우는** 일이 흔합니다. 기준선은 기다려 주지 않습니다."),
        dos: [
          tt("a.f06e6e69", "지금 내 채널의 구독자 · 90일 쇼츠 조회수 · 12개월 시청시간을 스튜디오에서 확인"),
          tt("a.13ccda10", "이미 파트너면 개정 약관 동의(2027-01-31까지)와 쇼츠 수익 모듈 수락 확인"),
          tt("a.0274e892", "조회수보다 **쇼핑 · 팬 펀딩 · 롱폼 전환** 구조를 목표로 잡기 — 쇼츠 광고는 100만 뷰에 수만 원"),
        ],
        donts: [
          tt("a.ee9a7088", "「2월에 새 기준으로 신청하지」 — 8,000시간 / 2,000만 회는 지금의 두 배입니다"),
          tt("a.cc550e8e", "쇼츠 광고 수익만 보고 채널 계획 세우기"),
        ],
        note: tt("a.academy.w0.note", "출처: 유튜브 공식 블로그(2026-08-11) · 고객센터 12843009. 배포 시점에 수치가 바뀌었을 수 있으니 링크를 함께 확인하세요."),
        quiz: {
          q: tt("a.9684c9ea", "2027년 1월 31일까지 파트너 프로그램에 가입하면, 2월 이후에도 기존 기준(롱폼 4,000시간 / 쇼츠 1,000만 회)이 그대로 적용되나요?"),
          answer: true,
          okText: tt("a.cf25ffae", "맞습니다. **1월 31일까지 가입**하면 기존 기준입니다. 그래서 지금 시작합니다."),
          badText: tt("a.401ef018", "1월 31일까지 가입한 채널은 기존 기준이 유지됩니다. 2월 1일부터 새로 신청하는 채널만 두 배 기준입니다."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w0.done", "OT 를 마쳤어요"),
        tone: "normal",
        core: tt("a.e39a9a25", "앞으로 7주, 매주 월요일 오전 10시에 카드 한 장이 열립니다. 핵심 3가지 · 30분 안쪽 무료 강의 · 과제 하나. **과제는 카톡 대화방에 올리는 것으로 끝**입니다."),
        outro: tt("a.c8ea3103", "무료 강의 링크와 이번 주 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 1,
    key: ACADEMY_LESSON_KEYS[1],
    title: tt("a.academy.w1.title", "채널 세팅: 수익화까지 가는 기본 설정"),
    goal: tt("a.academy.w1.goal", "브랜드 계정으로 채널을 만들고 수익화·고급 기능에 필요한 기본 설정을 끝냅니다."),
    points: [
      tt("a.8b925aca", "**브랜드 계정**으로 채널을 만드세요. 개인 계정과 분리되고 관리자를 추가할 수 있습니다."),
      tt("a.a074f5df", "채널명·프로필·채널아트·설명·링크는 「처음 온 사람이 3초 안에 무슨 채널인지 아는」 기준으로 씁니다."),
      tt("a.97d95689", "**2단계 인증과 전화번호 인증**을 지금 하세요. 맞춤 썸네일 · 고급 기능 · 수익화 신청의 전제 조건입니다."),
    ],
    sources: [
      src("에듀코카 · 1인 콘텐츠 실제 제작하기 1: 채널 개설 및 관리", tt("a.e12f3959", "로그인 없이 시청"), "https://edu.kocca.kr/edu/onlineEdu/openLecture/view.do?pSeq=767&menuNo=500085"),
      src("GSEEK · (기초)유튜브 따라잡기 — 유튜브 채널 운영", tt("a.471da355", "55분"), "https://www.gseek.kr/course/online/digital/social-networking-service/youtube-basics"),
    ],
    task: tt("a.e88c715f", "설정을 끝낸 채널 URL 을 대화방에 올려 주세요. 채널 설명 첫 줄을 서로 봐 드립니다. 체크: 브랜드 계정 · 설명·링크·비즈니스 이메일 · 2단계 인증 · 전화번호 인증 · 국가 설정."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w1.kicker", "1주차 · 채널 세팅"),
        title: tt("a.academy.w1.title", "채널 세팅: 수익화까지 가는 기본 설정"),
        tone: "normal",
        core: tt("a.21bfa15d", "**전화번호 인증**이 맞춤 썸네일·고급 기능·수익화 신청의 열쇠입니다. 구독자 수와 무관하게 지금 켤 수 있습니다."),
        caseText: tt("a.eff4ebbc", "채널을 한 달 운영하고도 썸네일이 자동 캡처로만 나가서 「왜 내 채널만 썸네일이 안 바뀌지」 하고 묻는 분이 많습니다. 전화번호 인증을 안 한 것이 이유였습니다."),
        dos: [
          tt("a.c01664b4", "브랜드 계정으로 채널 만들기 — 개인 계정과 분리, 관리자 추가 가능"),
          tt("a.de398277", "채널 설명 첫 줄에 「무슨 채널인지」 한 문장, 비즈니스 이메일·링크·국가 설정"),
          tt("a.8607a05b", "2단계 인증 + 전화번호 인증 → 맞춤 썸네일·고급 기능 즉시 해금"),
        ],
        donts: [
          tt("a.d2fa229f", "개인 계정으로 시작해 나중에 옮기려 하기 — 구독자·영상이 따라오지 않습니다"),
          tt("a.9d59928f", "「구독자가 늘면 하지」 하고 인증을 미루기"),
        ],
        quiz: {
          q: tt("a.939abeaf", "구독자가 1,000명이 안 되면, 전화번호 인증을 해도 맞춤 썸네일을 켤 수 없나요?"),
          answer: false,
          okText: tt("a.5d4f1410", "맞습니다. 구독자 수는 조건이 아닙니다. **전화번호 인증**만 하면 바로 열립니다."),
          badText: tt("a.20af451c", "구독자 수와 무관합니다. 전화번호 인증을 하면 맞춤 썸네일·고급 기능이 바로 열립니다."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w1.done", "1주차를 마쳤어요"),
        tone: "normal",
        core: tt("a.eb8510ad", "브랜드 계정 · 설명과 링크 · 2단계 인증 · 전화번호 인증. 이번 주 과제는 **설정을 끝낸 채널 URL 을 대화방에 올리기**입니다."),
        outro: tt("a.41a08e80", "무료 강의 링크와 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 2,
    key: ACADEMY_LESSON_KEYS[2],
    title: tt("a.academy.w2.title", "쇼츠는 어떻게 퍼지나: 유튜브가 직접 밝힌 것만"),
    goal: tt("a.academy.w2.goal", "유튜브가 공식적으로 밝힌 추천 신호를 이해하고, 떠도는 속설과 구분합니다."),
    points: [
      tt("a.81237cc0", "쇼츠는 세로·정사각 비율 **3분 이내** 영상입니다. 롱폼과 추천 엔진이 따로 돌아가고, 쇼츠를 올려도 롱폼 추천이 나빠지지 않습니다(공식)."),
      tt("a.dac68751", "추천을 결정하는 신호 3개: ① 시청을 선택한 비율(넘기지 않고 본 비율) ② 평균 시청 지속시간 ③ 평균 시청 비율. 좋아요·설문은 보조입니다."),
      tt("a.e076e1eb", "2025년 3월부터 조회수는 재생 즉시 집계됩니다. 수익과 파트너 자격은 **「유효 조회수」**로 봅니다. 화면에 보이는 조회수와 다릅니다."),
    ],
    sources: [
      src("고객센터 · Shorts 검색 및 발견 팁", tt("a.c0c4b303", "5분 읽기 · 공식"), "https://support.google.com/youtube/answer/11914225?hl=ko", "doc"),
      src("원초적 인사이트 · 쇼츠 제발 매일 올리지 마세요", tt("a.167ac5f4", "11분"), "https://www.youtube.com/watch?v=5VZ4uzWDp6s"),
      src("사이시옷 · 제품 책임자가 밝히는 쇼츠 알고리즘", tt("a.d1249dad", "글"), "https://brunch.co.kr/@96f07a76f1544a7/5", "doc"),
    ],
    task: tt("a.b548e3f6", "내 장르 키워드로 유튜브 검색 → 필터 「쇼츠 · 조회수순 · 이번 달」. 상위 5개의 첫 3초에 무엇이 나오는지 한 줄씩 적어 대화방에 올려 주세요."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w2.kicker", "2주차 · 알고리즘"),
        title: tt("a.academy.w2.title", "쇼츠는 어떻게 퍼지나: 유튜브가 직접 밝힌 것만"),
        tone: "normal",
        core: tt("a.0b02a255", "추천을 결정하는 것은 **넘기지 않고 본 비율 · 평균 시청 지속시간 · 평균 시청 비율** 셋입니다. 업로드 빈도는 랭킹 요소가 아닙니다(공식 문서)."),
        caseText: tt("a.969d0f7f", "「매일 올려야 알고리즘을 탄다」는 말에 하루 3개씩 올리다 품질이 떨어져 넘김 비율이 높아지고, 오히려 노출이 줄어든 채널이 많습니다. 유튜브는 빈도가 요소가 아니라고 문서에 적어 두었습니다."),
        dos: [
          tt("a.2e142999", "같은 품질로 주 3~5개 — 한 편 한 편 첫 3초와 완주율에 집중"),
          tt("a.0709d8da", "쇼츠 시청자의 약 74%는 비구독자 — 쇼츠는 새 사람이 들어오는 문"),
          tt("a.b0d13877", "성과는 「유효 조회수」로 보기(애널리틱스 고급 모드)"),
        ],
        donts: [
          tt("a.d507e0d6", "매일 올리려고 품질을 낮추기"),
          tt("a.56bd52f5", "「쇼츠 올리면 롱폼 추천이 죽는다」 — 공식적으로 아님"),
        ],
        quiz: {
          q: tt("a.cde7e619", "쇼츠는 매일 올려야 알고리즘을 타나요?"),
          answer: false,
          okText: tt("a.c2e1028c", "맞습니다. 유튜브 공식 문서에 **업로드 빈도는 랭킹 요소가 아니다**라고 적혀 있어요. 주 3~5개를 같은 품질로."),
          badText: tt("a.9d577f3e", "속설입니다. 공식 문서는 빈도가 요소가 아니라고 밝혔어요. 넘기지 않고 본 비율·시청 지속시간·시청 비율이 결정합니다."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w2.done", "2주차를 마쳤어요"),
        tone: "normal",
        core: tt("a.3d7583b0", "신호 3개 · 유효 조회수 · 빈도는 요소가 아님. 이번 주 과제는 **내 장르 상위 쇼츠 5개의 첫 3초 기록**입니다."),
        outro: tt("a.41a08e80", "무료 강의 링크와 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 3,
    key: ACADEMY_LESSON_KEYS[3],
    title: tt("a.academy.w3.title", "기획: 3초 훅과 끝까지 보게 하는 구조"),
    goal: tt("a.academy.w3.goal", "한 편에 한 메시지, 결론은 마지막에, 시작과 끝이 이어지는 구조로 대본을 짭니다."),
    points: [
      tt("a.b8ee2fb3", "첫 1~3초에 「안녕하세요」 금지. **한 문장 훅**으로 시작합니다 — 상식 뒤집기 / 결론 먼저 말하고 「어떻게?」 / 도전 과제, 셋 중 하나."),
      tt("a.84a685fd", "정답과 하이라이트는 **마지막**에. 답을 주면 바로 넘깁니다. 끝 장면이 첫 장면으로 이어지면 반복 시청이 생깁니다."),
      tt("a.fc97f1ba", "한 편에 메시지 하나. 「여행 갔는데」 대신 「200만 원짜리 호텔 가서」처럼 숫자와 구체어로, 상반신이 화면 중심에 오게."),
    ],
    sources: [
      src("에듀코카 · 숏폼 시대, 나만의 콘텐츠 기획하기 (김종래 PD)", tt("a.667dd4fc", "무료"), "https://edu.kocca.kr/edu/onlineEdu/openLecture/view.do?menuNo=500085&pSeq=1348"),
      src("작심만일 · 쇼츠를 초고속으로 알고리즘에 태우는 방법", tt("a.167ac5f4", "11분"), "https://www.youtube.com/watch?v=a9AEx4VELK8"),
      src("디렉터 에디 · 쇼츠 알고리즘 타는 법, 실제 채널 인증", tt("a.e6a71c56", "10분"), "https://www.youtube.com/watch?v=wpqD-JmGLnQ"),
    ],
    task: tt("a.25c3af0f", "대본 3개를 쓰세요(훅 한 문장 + 본문 + 마지막 한 방). 대화방에는 훅 한 줄만 3개 올려 주세요. 서로 「볼래 / 넘길래」로 답합니다."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w3.kicker", "3주차 · 기획"),
        title: tt("a.academy.w3.title", "기획: 3초 훅과 끝까지 보게 하는 구조"),
        tone: "normal",
        core: tt("a.10215a8f", "첫 3초에 **인사 대신 한 문장 훅**, 정답은 마지막에, 한 편에 메시지 하나. 시작과 끝이 이어지면 반복 시청이 생깁니다."),
        caseText: tt("a.f5fb7b93", "「안녕하세요, 오늘은 ○○ 얘기를 해 볼게요」로 시작한 쇼츠는 넘김 비율이 높고, 같은 내용을 「200만 원짜리 호텔에 갔는데」로 시작하자 시청 선택 비율이 크게 오른 사례가 반복됩니다."),
        dos: [
          tt("a.861556fe", "상식 뒤집기 / 결론 먼저 + 「어떻게?」 / 도전 과제 중 하나로 첫 문장"),
          tt("a.995ab690", "정답·하이라이트는 마지막에, 끝 장면이 첫 장면으로 이어지게"),
          tt("a.cda2b962", "「여행 갔는데」 대신 「200만 원짜리 호텔 가서」 — 숫자와 구체어, 상반신이 화면 중심"),
        ],
        donts: [
          tt("a.bf8017fc", "인사·자기소개로 시작하기"),
          tt("a.4a1f39e1", "한 편에 메시지 두 개 넣기 — 두 편으로 나눕니다"),
        ],
        quiz: {
          q: tt("a.fb441939", "쇼츠 첫 3초를 「안녕하세요, 오늘은 호텔 얘기를 해 볼게요」로 시작해도 괜찮나요?"),
          answer: false,
          okText: tt("a.b7355274", "맞습니다. 인사는 넘기기 신호입니다. **「200만 원짜리 호텔에 갔는데」**처럼 숫자와 구체어로 바로 들어갑니다."),
          badText: tt("a.c1d1ee66", "인사는 넘기기 신호예요. 숫자·구체어로 시작하는 한 문장 훅이 답입니다."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w3.done", "3주차를 마쳤어요"),
        tone: "normal",
        core: tt("a.022a6b8c", "훅 한 문장 · 정답은 마지막 · 메시지 하나. 이번 주 과제는 **대본 3개, 대화방에는 훅 한 줄만 3개**입니다."),
        outro: tt("a.41a08e80", "무료 강의 링크와 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 4,
    key: ACADEMY_LESSON_KEYS[4],
    title: tt("a.academy.w4.title", "촬영·편집: 스마트폰과 캡컷으로 끝내기"),
    goal: tt("a.academy.w4.goal", "추가 장비 없이 스마트폰 촬영과 캡컷 편집으로 첫 쇼츠를 완성합니다."),
    points: [
      tt("a.7cd634d9", "장비보다 **소리**. 세로 9:16, 창가 자연광, 삼각대 하나면 됩니다. 음질이 나쁘면 3초 안에 넘깁니다."),
      tt("a.aaf7e4f3", "자막은 **크고 짧게, 화면 가운데**. 시청의 60%가 무음이고 2배속으로 보는 사람도 많습니다. 화면은 3초마다 바뀌게."),
      tt("a.f92a1a5e", "편집은 캡컷 하나로 충분합니다. 컷 → 자동 자막 → 전환 → 1080p 내보내기. 자막 자동화는 브루(Vrew)."),
    ],
    sources: [
      src("인프런 · 폭발적인 조회수가 나오는 쇼츠 with 캡컷", tt("a.db06f343", "18강 36분 · 무료"), "https://www.inflearn.com/course/숏폼-영상-편집-캡컷"),
      src("에듀코카 · 캡컷 실전 편집 Part 1", tt("a.667dd4fc", "무료"), "https://edu.kocca.kr/edu/onlineEdu/openLecture/view.do?menuNo=500085&pSeq=1350"),
      src("GSEEK · 무료 앱으로 배우는 초간단 영상 편집, 캡컷", tt("a.72087471", "7차시 62분"), "https://www.gseek.kr/course/online/computer/video-editing/capcut-video-editing"),
    ],
    task: tt("a.4578d30b", "3주차 대본 중 하나를 촬영·편집해 30~60초 쇼츠를 완성하세요. 업로드는 5주차에 합니다. 완성본은 대화방에 파일로 올려 주세요."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w4.kicker", "4주차 · 촬영·편집"),
        title: tt("a.academy.w4.title", "촬영·편집: 스마트폰과 캡컷으로 끝내기"),
        tone: "normal",
        core: tt("a.a8bd08e4", "장비보다 **소리**, 자막은 **크고 짧게 가운데**, 화면은 3초마다. 편집은 캡컷 하나로 끝납니다."),
        caseText: tt("a.7890f17f", "카메라를 새로 사고도 에어컨 소리가 섞인 음성으로 올린 쇼츠는 3초 안에 넘겨졌고, 스마트폰으로 찍어도 자막을 크게 가운데 둔 쇼츠가 더 오래 시청된 일이 흔합니다. 시청의 60%는 소리를 끄고 봅니다."),
        dos: [
          tt("a.3503bc9d", "세로 9:16 · 창가 자연광 · 삼각대 하나 · 조용한 곳에서 음성"),
          tt("a.a7ce86a6", "자막 크고 짧게 화면 가운데 — 2배속·무음에도 읽히게, 3초마다 화면 전환"),
          tt("a.2a375fca", "캡컷: 컷 → 자동 자막 → 전환 → 1080p 내보내기 (자막 자동화는 브루)"),
        ],
        donts: [
          tt("a.07b5f4dd", "장비부터 사기 — 음질이 먼저"),
          tt("a.658daaa5", "자막을 작고 길게 아래쪽에 두기"),
        ],
        quiz: {
          q: tt("a.9e8fb8ec", "시청의 60%가 무음이니, 자막은 크고 짧게 화면 가운데에 두는 것이 맞나요?"),
          answer: true,
          okText: tt("a.bf0f6683", "맞습니다. **크고 짧게 가운데**, 3초마다 바뀌게. 2배속으로 봐도 읽혀야 합니다."),
          badText: tt("a.19e0db75", "무음·2배속 시청자가 놓칩니다. 자막은 크고 짧게 화면 가운데에 둡니다."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w4.done", "4주차를 마쳤어요"),
        tone: "normal",
        core: tt("a.a20eea6d", "소리 · 자막 · 캡컷. 이번 주 과제는 **3주차 대본 하나를 30~60초 쇼츠로 완성**하기입니다(업로드는 5주차)."),
        outro: tt("a.41a08e80", "무료 강의 링크와 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 5,
    key: ACADEMY_LESSON_KEYS[5],
    title: tt("a.academy.w5.title", "업로드: 제목, 설명, 썸네일, 연결"),
    goal: tt("a.academy.w5.goal", "첫 쇼츠를 올리며 메타데이터를 제대로 채우고, 롱폼과 연결합니다."),
    points: [
      tt("a.4d8ea708", "제목과 설명은 **검색어가 들어간 문장**으로. 「커버 영상」이 아니라 「○○ 후렴 30초 어쿠스틱 커버」. 처음엔 넓은 키워드보다 좁은 키워드."),
      tt("a.9122fd31", "쇼츠도 **썸네일**이 중요해졌습니다. 홈·탐색 화면 노출 비중이 커졌기 때문입니다. 맞춤 썸네일을 켜고 얼굴 하나, 텍스트 하나."),
      tt("a.2d7ca4ad", "「관련 동영상」에 내 롱폼을 연결하세요. 쇼츠로 들어온 사람이 롱폼으로 넘어가야 구독과 수익이 됩니다. **삭제 후 재업로드 금지**(스팸 판정 위험)."),
    ],
    sources: [
      src("에듀코카 · 1인 콘텐츠 실제 제작하기 4: 업로드 및 관리", tt("a.667dd4fc", "무료"), "https://edu.kocca.kr/edu/onlineEdu/openLecture/view.do?pSeq=770&menuNo=500085"),
      src("매력학자 동건 · 유튜브 쇼츠 알고리즘 정리 2026년 버전", tt("a.e6a71c56", "10분"), "https://www.youtube.com/watch?v=Fm14OwXemcY"),
      src("유튜브 공식 · Shorts 만들기", tt("a.1c73f88a", "문서"), "https://www.youtube.com/intl/ko_ALL/creators/shorts/", "doc"),
    ],
    task: tt("a.1c9651ca", "4주차에 만든 쇼츠를 올리세요. 제목·설명·관련 동영상까지 채운 뒤 링크를 대화방에 올려 주세요. 올라오면 서로 댓글 하나씩 남깁니다."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w5.kicker", "5주차 · 업로드"),
        title: tt("a.academy.w5.title", "업로드: 제목, 설명, 썸네일, 연결"),
        tone: "normal",
        core: tt("a.36337933", "제목·설명은 **검색어가 든 문장**, 썸네일은 얼굴 하나 텍스트 하나, 「관련 동영상」에 롱폼 연결. **지우고 다시 올리지 않습니다.**"),
        caseText: tt("a.6cbd4eb2", "반응이 없다고 쇼츠를 지우고 같은 파일을 다시 올리기를 반복하다 채널이 스팸으로 판정된 사례, 「커버 영상」이라는 제목만 달아 검색에 한 번도 안 잡힌 사례가 있습니다."),
        dos: [
          tt("a.c9900c8c", "「○○ 후렴 30초 어쿠스틱 커버」처럼 좁은 검색어가 든 문장형 제목·설명"),
          tt("a.be988950", "맞춤 썸네일 켜기 — 얼굴 하나, 텍스트 하나"),
          tt("a.3d6ac072", "「관련 동영상」에 내 롱폼 연결, 쇼츠·롱폼 주제 맞추기, 주 3~5개 같은 품질"),
        ],
        donts: [
          tt("a.61c39651", "삭제 후 재업로드 — 스팸 판정 위험"),
          tt("a.17709410", "영상과 무관한 해시태그·상품 태그, 하루 10개 몰아 올리기"),
        ],
        quiz: {
          q: tt("a.439be903", "반응이 안 좋은 쇼츠는 지우고 같은 영상을 다시 올리는 것이 좋은가요?"),
          answer: false,
          okText: tt("a.b1eeb656", "맞습니다. 삭제 후 재업로드는 **스팸 판정 위험**이 있어요. 그대로 두고 다음 편을 고칩니다."),
          badText: tt("a.f32bd14d", "삭제 후 재업로드는 스팸 판정 위험이 있습니다. 그대로 두고 다음 편에서 첫 3초를 바꿔 보세요."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w5.done", "5주차를 마쳤어요"),
        tone: "normal",
        core: tt("a.13fec1f3", "검색어 문장 · 썸네일 · 롱폼 연결 · 재업로드 금지. 이번 주 과제는 **4주차 쇼츠 업로드 후 링크 올리기**입니다."),
        outro: tt("a.41a08e80", "무료 강의 링크와 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 6,
    key: ACADEMY_LESSON_KEYS[6],
    title: tt("a.academy.w6.title", "분석: 스튜디오에서 딱 3개만 보기"),
    goal: tt("a.academy.w6.goal", "조회수가 아니라 유효 조회수·시청 비율·스와이프 지표로 다음 편을 고칩니다."),
    points: [
      tt("a.8621e37d", "조회수 말고 **「조회 vs 스와이프」**를 보세요. 첫 3초를 보고 넘기지 않은 사람의 비율입니다. 60% 이상이면 훅은 성공."),
      tt("a.7f638d8e", "**「평균 시청 비율」**이 70%면 양호, 80%면 우수. 낮으면 시청 지속 그래프에서 꺾이는 지점을 찾아 그 장면을 고칩니다."),
      tt("a.d01bef21", "반응의 무게는 공유 > 저장 > 재시청 > 댓글 > 좋아요. 처음 48~72시간 반응을 보고 그 뒤엔 다음 편에 집중합니다. 유효 조회수는 애널리틱스 고급 모드에서."),
    ],
    sources: [
      src("유튜브 공식 · 크리에이터 가이드 (분석 항목)", tt("a.1c73f88a", "문서"), "https://www.youtube.com/intl/ko/creators/how-things-work/", "doc"),
      src("고객센터 · 조회수 집계 방식", tt("a.fba6535b", "2026-08 갱신 · 공식"), "https://support.google.com/youtube/answer/2991785", "doc"),
      src("태그바이 · 조회수 100만보다 중요한 「이것」", tt("a.d1249dad", "글"), "https://www.openads.co.kr/content/contentDetail?contsId=19391", "doc"),
    ],
    task: tt("a.46aaab8a", "5주차 쇼츠의 애널리틱스에서 ① 조회 vs 스와이프 ② 평균 시청 비율 ③ 시청 지속 그래프를 캡처해 올리고, 「이탈 지점 한 곳 + 다음 편에서 바꿀 것 하나」를 적어 주세요."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w6.kicker", "6주차 · 분석"),
        title: tt("a.academy.w6.title", "분석: 스튜디오에서 딱 3개만 보기"),
        tone: "normal",
        core: tt("a.4bcaa399", "**조회 vs 스와이프 60% 이상**이면 훅 성공, **평균 시청 비율 70%** 양호 · 80% 우수. 반응의 무게는 공유 > 저장 > 재시청 > 댓글 > 좋아요."),
        caseText: tt("a.58d3ff67", "좋아요 수만 보고 「잘 됐다」고 판단한 쇼츠가 실제로는 넘김 비율이 60%를 넘어 노출이 끊긴 반면, 좋아요는 적어도 공유가 많던 쇼츠가 몇 주 뒤에 터진 일이 흔합니다."),
        dos: [
          tt("a.a08848d8", "조회 vs 스와이프 → 60% 이상이면 훅 성공, 아니면 첫 3초부터 수정"),
          tt("a.2f0a2a2f", "평균 시청 비율이 낮으면 시청 지속 그래프의 꺾이는 지점 한 곳만 고치기"),
          tt("a.0209372d", "48~72시간 반응 본 뒤 다음 편에 집중 — 유효 조회수는 고급 모드에서"),
        ],
        donts: [
          tt("a.1a587110", "좋아요 수로 성패 판단하기 — 가장 약한 신호"),
          tt("a.fa9c595b", "실시간 조회수에 매달리기 — 몇 주 뒤 터지는 쇼츠도 있습니다"),
        ],
        quiz: {
          q: tt("a.c6dbf6f1", "「조회 vs 스와이프」가 60% 이상이면 첫 3초 훅이 성공한 것으로 봐도 되나요?"),
          answer: true,
          okText: tt("a.d563108b", "맞습니다. **넘기지 않고 본 비율**이 훅의 성적표입니다. 그다음은 평균 시청 비율을 봅니다."),
          badText: tt("a.f6f4f488", "60% 이상이면 훅은 성공입니다. 좋아요 수가 아니라 넘기지 않은 비율이 기준이에요."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w6.done", "6주차를 마쳤어요"),
        tone: "normal",
        core: tt("a.755fcc31", "조회 vs 스와이프 · 평균 시청 비율 · 공유 > 저장 > 재시청. 이번 주 과제는 **애널리틱스 3장 캡처 + 이탈 지점 한 곳**입니다."),
        outro: tt("a.41a08e80", "무료 강의 링크와 과제는 아카데미 페이지의 이 주차 상세에 있어요."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
  {
    index: 7,
    key: ACADEMY_LESSON_KEYS[7],
    title: tt("a.academy.w7.title", "수익화: 조건, 구조, 그리고 광고 말고"),
    goal: tt("a.academy.w7.goal", "YPP 와 쇼츠 수익 구조를 정확히 알고, 광고 외 수익원을 채널 단계별로 설계합니다."),
    points: [
      tt("a.b11f36f1", "파트너 프로그램 조건: 구독자 1,000명 + (12개월 롱폼 4,000시간 또는 90일 쇼츠 유효 조회수 1,000만). **2027년 2월 1일부터 8,000시간 / 2,000만.**"),
      tt("a.5b29d8f7", "그 전에 열리는 문: 구독자 500명 + 90일 내 업로드 3개 + (12개월 3,000시간 또는 90일 쇼츠 300만)이면 **멤버십·슈퍼챗·쇼핑**이 열립니다. 이 기준은 바뀌지 않습니다."),
      tt("a.3763bb08", "쇼츠 광고 수익은 조회수 100만에 수만 원입니다. 실제 수익은 쇼핑·제휴 → 멤버십·슈퍼챗 → 자체 상품 순으로 만듭니다. 쇼츠만 하는 채널은 롱폼을 병행해야 2027년 이후에도 수익이 남습니다. 애드센스 연결과 세금 정보(W-8BEN)를 안 내면 24% 원천징수."),
    ],
    sources: [
      src("고객센터 · YPP 개요 및 자격 요건", tt("a.47e63c8d", "공식"), "https://support.google.com/youtube/answer/72851?hl=ko", "doc"),
      src("준휘야 쇼츠하자 · 유튜브가 공개한 쇼츠로 수익 10배", tt("a.547bb7ff", "15분"), "https://www.youtube.com/watch?v=d3GDAcBYjUc"),
      src("캐럿 · 쇼츠 수익창출 조건 총정리", tt("a.38eaa7de", "글 · 2026-04 갱신"), "https://carat.im/blog/youtube-shorts-revenue-conditions-methods", "doc"),
      src("StartBucks · RPM 부터 실수령액까지", tt("a.b7d51fb1", "글 · 2026-07"), "https://www.startbucks.co.kr/post.html?slug=youtube-rpm-real-earnings", "doc"),
    ],
    task: tt("a.5b0e7c81", "OT 때 캡처한 화면을 다시 캡처해 나란히 올려 주세요. 그리고 내 채널 단계(500 미만 / 500~1,000 / 1,000 이상)에 맞는 수익원 하나를 골라 30일 계획을 세 줄로 적어 주세요."),
    chapters: [
      {
        key: "main",
        kicker: tt("a.academy.w7.kicker", "7주차 · 수익화"),
        title: tt("a.academy.w7.title", "수익화: 조건, 구조, 그리고 광고 말고"),
        tone: "normal",
        core: tt("a.ba2eb424", "광고보다 먼저 열리는 문이 있습니다. **구독자 500 + 90일 업로드 3개 + (3,000시간 또는 쇼츠 300만)** 이면 멤버십·슈퍼챗·쇼핑. 파트너 프로그램(광고)은 1,000명 + 4,000시간 / 1,000만."),
        caseText: tt("a.9bdd099b", "「구독자 1,000명이 될 때까지는 수익이 없다」고 믿고 500명 문을 그냥 지나친 채널이 많습니다. 반대로 쇼츠 광고 수익만 기다리다 100만 뷰에 수만 원을 받고 실망하는 일도 흔합니다."),
        dos: [
          tt("a.5c14ceac", "내 채널 단계(500 미만 / 500~1,000 / 1,000 이상)에 맞는 수익원 하나부터"),
          tt("a.fb64a706", "쇼핑·제휴 → 멤버십·슈퍼챗 → 자체 상품 순으로 다각화, 쇼츠만이면 롱폼 병행"),
          tt("a.bdd406f3", "애드센스 연결 + 세금 정보(W-8BEN) 제출 — 안 하면 24% 원천징수"),
        ],
        donts: [
          tt("a.d45677f9", "쇼츠 광고 수익만 보고 채널 운영하기"),
          tt("a.fa3b3385", "영상과 무관한 상품 태그 반복 — 쇼핑 권한이 박탈됩니다"),
        ],
        note: tt("a.academy.w7.note", "출처: 고객센터 72851(YPP) · 12504220(쇼츠 수익). 2027-02-01 부터 파트너 프로그램 신규 기준은 8,000시간 / 2,000만 회."),
        quiz: {
          q: tt("a.45e0912d", "구독자 500명 + 90일 업로드 3개 + 쇼츠 300만 회를 채우면, 광고 수익이 바로 들어오나요?"),
          answer: false,
          okText: tt("a.9f31876f", "맞습니다. 그 문으로 열리는 건 **멤버십·슈퍼챗·쇼핑**입니다. 광고 수익은 파트너 프로그램(1,000명) 뒤에 옵니다."),
          badText: tt("a.80eb3060", "광고 수익은 파트너 프로그램(구독자 1,000명 + 4,000시간 / 1,000만) 뒤입니다. 500명 문으로 먼저 열리는 것은 멤버십·슈퍼챗·쇼핑이에요."),
        },
      },
      {
        key: "done",
        kicker: tt("a.academy.done.kicker", "완료"),
        title: tt("a.academy.w7.done", "7주를 모두 마쳤어요"),
        tone: "normal",
        core: tt("a.6d447eb3", "기초 과정 끝. 이번 주 과제는 **OT 캡처와 지금 캡처를 나란히 + 30일 계획 세 줄**입니다. 심화 트랙은 아카데미 페이지에서 언제든지."),
        outro: tt("a.9afe986d", "여기까지 온 채널은 2027년 2월 기준 변경 전에 준비를 마친 채널입니다."),
        guideSlugs: [{ slug: ACADEMY_GUIDE_SLUG, label: tt("a.academy.guide", "아카데미 안내") }],
      },
    ],
  },
]);

/** 심화 트랙 — 이수 기록 없음, 링크만 */
export const ACADEMY_ADVANCED = lazyI18n(() => [
  { label: tt("a.528e0af9", "K-MOOC · AI활용 숏폼미디어크리에이터로 수익화하기 (경일대)"), meta: tt("a.84b5e997", "15주 45시간 · 무료"), href: "https://www.kmooc.kr/view/course/detail/18312", kind: "video" },
  { label: "Coursera · YouTube Shorts: Create Winning Content", meta: tt("a.academy.adv.en", "영어 · 청강 무료"), href: "https://www.coursera.org/learn/youtube-shorts-create-winning-content", kind: "video" },
  { label: tt("a.40358d1d", "Meta Blueprint · 릴스 만들기"), meta: tt("a.db4a7a77", "15분 · 숏폼 원리 공통"), href: "https://www.facebookblueprint.com/student/collection/506997-create-reels", kind: "video" },
  { label: "HubSpot Academy · YouTube Marketing", meta: tt("a.academy.adv.en2", "영어 · 2시간"), href: "https://academy.hubspot.com/courses/youtube-marketing", kind: "video" },
]);

export const getAcademyWeek = (i) => ACADEMY_WEEKS[i] || null;
export const getAcademyWeekByKey = (lessonKey) => {
  const i = academyWeekIndexOf(lessonKey);
  return i === null ? null : ACADEMY_WEEKS[i];
};

/** LESSONS 레지스트리에 스프레드할 항목 — 세트마다 같은 버전, 장은 주차 데이터의 chapters */
export function academyLessonEntries() {
  const out = {};
  ACADEMY_WEEKS.forEach((w) => {
    out[w.key] = {
      key: w.key,
      version: ACADEMY_VERSION,
      chapters: w.chapters,
      title: `${academyWeekLabel(w.index)} · ${w.title}`,
      context: tt("a.academy.lesson.context", "레이블리 아카데미"),
      estimate: tt("a.academy.lesson.estimate", "약 5분"),
      completeLabel: tt("a.lesson.finish", "레슨 마치기"),
    };
  });
  return out;
}

export const academyLessonLabels = () => {
  const out = {};
  ACADEMY_LESSON_KEYS.forEach((k, i) => { out[k] = tt("a.academy.label", "아카데미 {{v0}}", { v0: academyWeekLabel(i) }); });
  return out;
};

// ── 공개 규칙 (§5) ─────────────────────────────────────────────────────────
// 서버 RPC academy_state() 가 주는 값: { enabled, start_at, open_mode: 'drip'|'open', week_index, open_upto, server_now }
// 클라이언트도 같은 산식을 가진다 — 서버 값이 없을 때(미리보기) 와 테스트용.

/** 시작일 기준 몇 주째인가 (0 = 첫 주 = OT). 시작 전이면 -1. 잘못된 입력은 -1 */
export function academyWeekIndex(now, startAt) {
  const n = now instanceof Date ? now : new Date(now || Date.now());
  const s = startAt instanceof Date ? startAt : new Date(startAt);
  if (!startAt || Number.isNaN(s.getTime()) || Number.isNaN(n.getTime())) return -1;
  const diff = n.getTime() - s.getTime();
  if (diff < 0) return -1;
  return Math.floor(diff / WEEK_MS);
}

/** 열린 마지막 주차 번호(0..8). 전부 공개면 8, 시작 전이면 -1 */
export function academyOpenUpto({ mode = "drip", weekIndex = -1 } = {}) {
  if (mode === "open") return ACADEMY_WEEK_COUNT - 1;
  if (weekIndex < 0) return -1;
  return Math.min(weekIndex, ACADEMY_WEEK_COUNT - 1);
}

export const isAcademyWeekOpen = (i, openUpto) => i >= 0 && i <= openUpto;

/** 다음 카드가 열리는 시각 (drip · 아직 남은 주차가 있을 때). 없으면 null */
export function academyNextOpenAt({ startAt, openUpto }) {
  if (!startAt || openUpto >= ACADEMY_WEEK_COUNT - 1) return null;
  const s = new Date(startAt);
  if (Number.isNaN(s.getTime())) return null;
  return new Date(s.getTime() + (openUpto + 1) * WEEK_MS);
}

/**
 * 진행 상태 — completions: work_lesson_completion 행들(내 것). 현재 버전 행만 이수로 본다.
 *   done: 이수한 주차 번호들 · current: 홈 카드가 가리킬 주차(drip = 이번 주, open = 첫 미이수) · allDone
 */
export function academyProgress({ completions = [], openUpto = -1, mode = "drip" } = {}) {
  const rows = Array.isArray(completions) ? completions : [];
  const done = [];
  ACADEMY_LESSON_KEYS.forEach((k, i) => {
    if (rows.some((r) => r && r.lesson_key === k && r.lesson_version === ACADEMY_VERSION && r.trigger !== "self")) done.push(i);
  });
  const allDone = done.length === ACADEMY_WEEK_COUNT;
  let current = null;
  if (openUpto >= 0) {
    if (mode === "open") {
      for (let i = 0; i <= openUpto; i += 1) { if (!done.includes(i)) { current = i; break; } }
    } else {
      current = openUpto;
    }
  }
  return { done, current, allDone, doneCount: done.length, total: ACADEMY_WEEK_COUNT };
}

/** ?week=N — 0..8 정수만. 그 밖은 null */
export function parseWeekParam(search) {
  const v = new URLSearchParams(search || "").get("week");
  if (v === null || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 && n < ACADEMY_WEEK_COUNT ? n : null;
}

/** 첫 진입 선택(§4-2): ?week → 이번 주(drip) / 첫 미이수(open) → 전부 이수면 8주차 → 열린 게 없으면 0 */
export function pickInitialWeek({ weekParam = null, openUpto = -1, mode = "drip", progress } = {}) {
  if (weekParam !== null && isAcademyWeekOpen(weekParam, openUpto)) return weekParam;
  if (openUpto < 0) return 0;
  if (progress && progress.allDone) return ACADEMY_WEEK_COUNT - 1;
  if (progress && progress.current !== null && progress.current !== undefined) return progress.current;
  return openUpto;
}
